import React from 'react';
import { theme } from '../../styles/theme';
import {
  COMPONENT_SIZES,
  COMPONENT_VARIANTS,
  ACCESSIBILITY,
  ButtonVariant,
  ComponentSize,
} from '../../styles/constants';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ComponentSize;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  ...props
}) => {
  const baseClasses = `
    inline-flex items-center justify-center
    font-medium rounded-md transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:opacity-60 disabled:cursor-not-allowed
    ${fullWidth ? 'w-full' : ''}
    ${loading ? 'cursor-wait' : ''}
  `;

  const sizeClasses = {
    xs: 'px-2 py-1 text-xs min-h-[1.5rem]',
    sm: 'px-3 py-1.5 text-sm min-h-[2rem]',
    md: 'px-4 py-2 text-sm min-h-[2.5rem]',
    lg: 'px-6 py-3 text-base min-h-[3rem]',
    xl: 'px-8 py-4 text-lg min-h-[3.5rem]',
  };

  const variantClasses = {
    primary: `
      bg-primary-600 hover:bg-primary-700 active:bg-primary-800
      text-white border border-transparent
      focus:ring-primary-500
    `,
    secondary: `
      bg-secondary-100 hover:bg-secondary-200 active:bg-secondary-300
      text-secondary-900 border border-secondary-200
      focus:ring-secondary-500
    `,
    outline: `
      bg-transparent hover:bg-primary-50 active:bg-primary-100
      text-primary-700 border border-primary-300 hover:border-primary-400
      focus:ring-primary-500
    `,
    ghost: `
      bg-transparent hover:bg-secondary-100 active:bg-secondary-200
      text-secondary-700 border border-transparent
      focus:ring-secondary-500
    `,
    link: `
      bg-transparent hover:bg-transparent active:bg-transparent
      text-primary-600 hover:text-primary-700 underline-offset-4
      hover:underline border border-transparent
      focus:ring-primary-500 px-0
    `,
  };

  const combinedClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${className}
  `;

  return (
    <button
      className={combinedClasses}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}

      {!loading && leftIcon && (
        <span className="mr-2 flex items-center">{leftIcon}</span>
      )}

      <span className={loading ? 'opacity-70' : ''}>{children}</span>

      {!loading && rightIcon && (
        <span className="ml-2 flex items-center">{rightIcon}</span>
      )}
    </button>
  );
};

export default Button;
